import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ContactDetailsComponent } from './ContactDetails/contact-details.component';
import { ThankYouComponent } from './ThankYou/thank-you.component';
import { GetQuoteComponent } from './1-GetQuote/get-quote.component';
import { GetHelpLoginComponent } from './4-GetHelpLogIn/get-help-login.component';
import { ReqPolicyDocComponent } from './5-ReqPolicyDoc/req-policy-doc.component';
import { ReportClaimComponent } from './2-ReportClaim/report-claim.component';
import { ReqLossRunComponent } from './3-ReqLostRun/req-loss-run.component';
import { ReqCommissionComponent } from './6-RegCommission/req-commission.component';
import { QuesBillPaymentComponent } from './7-QuesBillPayment/ques-bill-payment.component';
import { BecomeAppointedComponent } from './8-BecomeAppointed/become-appointed.component';
import { CareersComponent } from './9-Careers/careers.component';
import { AskProductComponent } from './10-AskProduct/ask-product.component';
import { EmpVeriComponent } from './11-EmpVeri/emp-veri.component';
import { ComplaintsComponent } from './12-Complaints/complaints.component';
import { OtherComponent } from './13-Other/other.component';
import { ContactMxComponent } from './ContactMX/contact-mx.component';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ContactUsService } from './core/services/contact-us.service';
import { LanguageService } from './core/services/language.service';

@Component({
  selector: 'app-root',
  imports: [CommonModule, ContactDetailsComponent, ContactMxComponent, GetQuoteComponent, ReactiveFormsModule, GetHelpLoginComponent, ReqPolicyDocComponent, ReportClaimComponent, ReqLossRunComponent, ReqCommissionComponent, QuesBillPaymentComponent, BecomeAppointedComponent, CareersComponent, AskProductComponent, EmpVeriComponent, ComplaintsComponent, OtherComponent, ThankYouComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent {

  @ViewChild(ContactDetailsComponent)
  contactDetailsComponent: ContactDetailsComponent;

  @ViewChild(ContactMxComponent)
  contactMxComponent: ContactMxComponent;

  @ViewChild(GetQuoteComponent)
  getQuoteComponent: GetQuoteComponent;

  @ViewChild(AskProductComponent)
  askProductComponent: AskProductComponent;

  parentFormGroup: FormGroup;
  formSubmitted = false;
  dictionaryLoaded = false;
  showValidationError = false;
  showSubmissionError = false;
  lang: string = '';

  get isMxLang(): boolean {
    return ['en-mx', 'es-mx'].includes(this.lang);
  }

  get labels() { return this.contactUsService.commonLabels; }
  get placeholders() { return this.contactUsService.placeholderTextLabels; }
  get messages() { return this.contactUsService.messageLabels; }

  get listLabels() { return this.contactUsService.listLabels; }

  inquiryTypes: { key: string; label: string }[] = [];

  readonly CONTACT_DETAILS_KEYS = ['getAQuote', 'getHelpLoggingIn', 'requestPolicyDoc', 'requestCommission', 'questionBillPayment', 'becomeAppointed', 'askAboutProduct', 'other'];

  constructor(private formBuilder: FormBuilder, private router: Router, private contactUsService: ContactUsService, private languageService: LanguageService) {
    this.parentFormGroup = this.formBuilder.group({
      inquiryType: ['']
    });

    this.parentFormGroup.get('inquiryType')?.valueChanges.subscribe((value) => {
      this.removeChildForm('getquote');
      this.removeChildForm('reportclaim');
      this.removeChildForm('loginhelp');
      this.removeChildForm('policydoc');
      this.removeChildForm('commission');
      this.removeChildForm('billpayment');
      this.removeChildForm('appointed');
      this.removeChildForm('career');
      this.removeChildForm('askproduct');
      this.removeChildForm('other');

      // Only remove contactinfo if new selection doesn't need it
      if (!this.CONTACT_DETAILS_KEYS.includes(value)) {
        this.removeChildForm('contactinfo');
      }

      // Reset remaining child forms (except contactinfo to preserve turnstile token)
      Object.keys(this.parentFormGroup.controls).forEach(key => {
        const control = this.parentFormGroup.get(key);
        if (key !== 'inquiryType' && key !== 'contactinfo' && control instanceof FormGroup) {
          control.reset();
        }
      });
    });
  }

  ngOnInit() {
    const lang = this.languageService.getLanguageFromUrl();
    this.lang = lang;
    this.contactUsService.getDictionary(lang).subscribe(() => {
      const inquiryTypeObj = this.listLabels.inquiryType;
      this.inquiryTypes = Object.keys(inquiryTypeObj).map(key => ({
        key,
        label: (inquiryTypeObj as any)[key]
      }));
      this.dictionaryLoaded = true;
    });

    this.parentFormGroup.statusChanges.subscribe(status => {
      if (status === 'VALID') {
        this.showValidationError = false;
      }
    });
  }

  addChildForm(name: string, group: FormGroup) {
    this.parentFormGroup.addControl(name, group);
  }

  removeChildForm(name: string) {
    if (this.parentFormGroup.contains(name)) {
      this.parentFormGroup.removeControl(name);
    }
  }

  onSubmit(): void {
    if (this.parentFormGroup.valid) {
      this.showValidationError = false;
      this.showSubmissionError = false;
      if (this.contactDetailsComponent) {
        this.contactDetailsComponent.loaderIcon = true;
      }
      if (this.contactMxComponent) {
        this.contactMxComponent.loaderIcon = true;
      }
      const lang = this.languageService.getLanguageFromUrl();
      const formData = { ...this.parentFormGroup.value, lang };
      if (this.isMxLang) {
        formData.inquiryType = 'ContactMX';
        formData.region = 'MX';
      }
      if (formData.contactinfo) {
        const { confirmEmail, agreeTerms, turnstile, ...contactInfo } = formData.contactinfo;
        formData.contactinfo = contactInfo;
      }
      // Extract region from child form and move to top level
      const childKeys = ['getquote', 'askproduct', 'policydoc', 'commission', 'appointed', 'loginhelp', 'billpayment', 'other'];
      for (const key of childKeys) {
        if (formData[key]?.region !== undefined) {
          formData.region = formData[key].region;
          delete formData[key].region;
          break;
        }
      }
      if (formData.getquote) {
        if (formData.getquote.productCategoryId === 'other') {
          formData.getquote.productCategory = '';
          formData.getquote.productCategoryId = '';
          formData.getquote.product = '';
          formData.getquote.productId = '';
        } else if (formData.getquote.productId === 'other') {
          formData.getquote.product = '';
          formData.getquote.productId = '';
        }
      }
      if (formData.askproduct) {
        if (formData.askproduct.productCategoryId === 'other') {
          formData.askproduct.productCategory = '';
          formData.askproduct.productCategoryId = '';
          formData.askproduct.product = '';
          formData.askproduct.productId = '';
        } else if (formData.askproduct.productId === 'other') {
          formData.askproduct.product = '';
          formData.askproduct.productId = '';
        }
      }
      if (formData.policydoc) {
        if (formData.policydoc.productCategoryId === 'other') {
          formData.policydoc.productCategory = '';
          formData.policydoc.productCategoryId = '';
          formData.policydoc.product = '';
          formData.policydoc.productId = '';
        } else if (formData.policydoc.productId === 'other') {
          formData.policydoc.product = '';
          formData.policydoc.productId = '';
        }
      }
      if (formData.billpayment) {
        if (formData.billpayment.productCategoryId === 'other') {
          formData.billpayment.productCategory = '';
          formData.billpayment.productCategoryId = '';
          formData.billpayment.product = '';
          formData.billpayment.productId = '';
        } else if (formData.billpayment.productId === 'other') {
          formData.billpayment.product = '';
          formData.billpayment.productId = '';
        }
      }
      if (formData.appointed) {
        if (Array.isArray(formData.appointed.productCategory)) {
          formData.appointed.productCategory = formData.appointed.productCategory.join(', ');
        }
        if (Array.isArray(formData.appointed.productCategoryId)) {
          formData.appointed.productCategoryId = formData.appointed.productCategoryId.join(', ');
        }
      }
      this.contactUsService.submitContactForm(formData).subscribe({
        next: (response) => {
          console.log('Form submitted successfully:', response);
          this.formSubmitted = true;
          setTimeout(() => {
            window.scrollTo(0, 0);
            document.body.scrollTop = 0;
            document.documentElement.scrollTop = 0;
          });
          if (this.contactDetailsComponent) {
            this.contactDetailsComponent.loaderIcon = false;
          }
          if (this.contactMxComponent) {
            this.contactMxComponent.loaderIcon = false;
          }
        },
        error: (error) => {
          console.error('Form submission failed:', error);
          this.showSubmissionError = true;
          if (this.contactDetailsComponent) {
            this.contactDetailsComponent.loaderIcon = false;
          }
          if (this.contactMxComponent) {
            this.contactMxComponent.loaderIcon = false;
          }
        }
      });
    } else {
      this.showValidationError = true;
      this.markAllAsTouched();
      this.scrollToFirstInvalidControl();
    }
  }

  private scrollToFirstInvalidControl(): void {
    const firstInvalid = document.querySelector('.ng-invalid[formControlName]') as HTMLElement;
    if (firstInvalid) {
      firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      firstInvalid.focus();
    }
  }

  private markAllAsTouched(): void {
    Object.keys(this.parentFormGroup.controls).forEach(key => {
      const control = this.parentFormGroup.get(key);
      if (control instanceof FormGroup) {
        Object.keys(control.controls).forEach(childKey => {
          control.get(childKey)?.markAsTouched();
        });
      } else {
        control?.markAsTouched();
      }
    });
  }
}